import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckLg, XLg, PlusLg } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import AdminLayout, { isStaff } from '../AdminLayout';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../../common/ui/notify';
import { display, formatPrice } from '../../../common/format';
import BidderPicker from './BidderPicker';
import './AdminAuction.css';

const L = { geo: 0, en: 1, ru: 2, am: 3 };
const t = (geo, en, ru, am) => [geo, en, ru, am][L[currentLanguage()] ?? 0];

const TABS = ['', 'pending', 'confirmed', 'rejected', 'refunded'];
const TONE = { pending: 'cx-badge-new', confirmed: 'cx-badge-sold', rejected: 'cx-badge-danger', refunded: 'cx-badge-neutral' };

const AdminAuctionDeposits = () => {
    const user = useSelector((s) => s.user);
    const [status, setStatus] = useState('');
    const [rows, setRows] = useState([]);
    const [state, setState] = useState('loading');
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ user_id: '', amount: '', reference: '', confirm: true });
    const [bidders, setBidders] = useState([]);

    const load = useCallback(() => {
        setState('loading');
        axios.get(`/api/auction/admin/deposits${status ? `?status=${status}` : ''}`)
            .then((res) => { setRows(res.data.deposits || []); setState('ready'); })
            .catch(() => setState('error'));
    }, [status]);

    useEffect(() => { if (isStaff(user)) load(); }, [load, user]);
    useEffect(() => {
        if (isStaff(user)) axios.get('/api/auction/admin/bidders').then((r) => setBidders(r.data.bidders || [])).catch(() => {});
    }, [user]);

    const record = async (e) => {
        e.preventDefault();
        if (!form.user_id) { notify.warning(t('აირჩიეთ ბიდერი', 'Choose a bidder', 'Выберите бидера', 'Ընտրեք բիդերին')); return; }
        setBusy(true);
        try {
            await axios.post('/api/auction/admin/deposits', {
                user_id: Number(form.user_id), amount: Number(form.amount), reference: form.reference, confirm: form.confirm,
            });
            notify.success(t('დეპოზიტი დაფიქსირდა', 'Deposit recorded', 'Депозит записан', 'Ավանդը գրանցվեց'));
            setForm({ user_id: '', amount: '', reference: '', confirm: true });
            load();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const setStatusOf = async (id, newStatus) => {
        setBusy(true);
        try {
            await axios.post(`/api/auction/admin/deposits/${id}/confirm`, { status: newStatus });
            notify.success(t('განახლდა', 'Updated', 'Обновлено', 'Թարմացվեց'));
            load();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const body = () => {
        if (state === 'loading') return <LoadingBlock label={t('იტვირთება...', 'Loading...', 'Загрузка...', 'Բեռնվում...')} />;
        if (state === 'error') return <ErrorBlock title={t('შეცდომა', 'Error', 'Ошибка', 'Սխալ')} retryLabel={t('თავიდან', 'Retry', 'Повтор', 'Կրկին')} onRetry={load} />;
        if (!rows.length) return <EmptyBlock title={t('დეპოზიტები არ არის', 'No deposits', 'Нет депозитов', 'Ավանդներ չկան')} />;
        return (
            <div className="admin-table-wrapper">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t('ბიდერი', 'Bidder', 'Бидер', 'Բիդեր')}</th>
                            <th>{t('თანხა', 'Amount', 'Сумма', 'Գումար')}</th>
                            <th className="admin-hide-sm">{t('მეთოდი', 'Method', 'Метод', 'Մեթոդ')}</th>
                            <th className="admin-hide-md">{t('რეფ.', 'Ref.', 'Реф.', 'Հղում')}</th>
                            <th>{t('სტატუსი', 'Status', 'Статус', 'Կարգավիճակ')}</th>
                            <th className="admin-table-actions-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((d) => (
                            <tr key={d.id}>
                                <td>
                                    <span className="admin-table-primary">{[d.name, d.surname].filter(Boolean).join(' ') || `#${d.user_id}`}</span>
                                    <span className="admin-table-secondary">{display(d.email)} · id {d.user_id}</span>
                                </td>
                                <td><strong>{formatPrice(d.amount, d.currency || 'GEL')}</strong></td>
                                <td className="admin-hide-sm">{display(d.method)}</td>
                                <td className="admin-hide-md admin-table-secondary">{display(d.reference)}</td>
                                <td><span className={`cx-badge ${TONE[d.status] || 'cx-badge-neutral'}`}>{d.status}</span></td>
                                <td>
                                    {d.status === 'pending' && (
                                        <div className="admin-row-actions">
                                            <button className="admin-icon-button" disabled={busy} title={t('დადასტურება', 'Confirm', 'Подтвердить', 'Հաստատել')} onClick={() => setStatusOf(d.id, 'confirmed')}><CheckLg /></button>
                                            <button className="admin-icon-button is-danger" disabled={busy} title={t('უარყოფა', 'Reject', 'Отклонить', 'Մերժել')} onClick={() => setStatusOf(d.id, 'rejected')}><XLg /></button>
                                        </div>
                                    )}
                                    {d.status === 'confirmed' && (
                                        <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => setStatusOf(d.id, 'refunded')}>{t('დაბრუნება', 'Refund', 'Возврат', 'Վերադարձ')}</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <AdminLayout title={t('დეპოზიტები', 'Deposits', 'Депозиты', 'Ավանդներ')}
            subtitle={t('ხელით საბანკო დეპოზიტების დადასტურება', 'Confirm manual bank deposits', 'Подтверждение банковских депозитов', 'Բանկային ավանդների հաստատում')}>
            <form className="cx-card admin-auction-form" onSubmit={record}>
                <h4>{t('ახალი დეპოზიტი', 'Record a deposit', 'Записать депозит', 'Գրանցել ավանդ')}</h4>
                <div className="admin-auction-form-row">
                    <BidderPicker
                        bidders={bidders}
                        value={form.user_id}
                        onChange={(id) => setForm((p) => ({ ...p, user_id: id }))}
                        placeholder={t('აირჩიეთ ბიდერი', 'Choose a bidder', 'Выберите бидера', 'Ընտրեք բիդերին')}
                    />
                    <input className="cx-input" type="number" min="0" step="0.01" required placeholder={t('თანხა (GEL)', 'Amount (GEL)', 'Сумма (GEL)', 'Գումար (GEL)')} value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
                    <input className="cx-input" placeholder={t('რეფერენსი', 'Reference', 'Референс', 'Հղում')} value={form.reference} onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))} />
                    <label className="admin-auction-check">
                        <input type="checkbox" checked={form.confirm} onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.checked }))} />
                        {t('დადასტურებული', 'Confirmed', 'Подтверждён', 'Հաստատված')}
                    </label>
                    <button className="cx-btn cx-btn-primary" disabled={busy}><PlusLg /> {t('დამატება', 'Add', 'Добавить', 'Ավելացնել')}</button>
                </div>
            </form>

            <div className="admin-status-tabs" role="tablist">
                {TABS.map((tab) => (
                    <button key={tab || 'all'} type="button" role="tab" aria-selected={status === tab}
                        className={`admin-status-tab ${status === tab ? 'is-active' : ''}`} onClick={() => setStatus(tab)}>
                        {tab || t('ყველა', 'all', 'все', 'բոլորը')}
                    </button>
                ))}
            </div>

            {body()}
        </AdminLayout>
    );
};

export default AdminAuctionDeposits;
